import { Logger, useAppLoader, useLogout } from '@capsicum/common';
import { privateKeyHexAtom, useDeleteAccount } from '@capsicum/socios-core';
import { useAtom } from 'jotai';
import { usePauseCredentialsWebSocketsListener } from 'libs/features/regulations/shared/src/hooks/credentials-web-sockets/usePauseCredentialsWebSocketsListener';
import { useEffect, useRef, useState } from 'react';

import { type ExportAndDeleteAccountPageContentType } from '../../models';

export const useExportAndCloseAccountPage = ({
	exportWallet,
	initContent = 'warning',
	openErrorDrawer,
	openTransferFirstWarningModal
}: {
	exportWallet: () => Promise<{ privateKeyHexString: string }>;
	initContent: Extract<ExportAndDeleteAccountPageContentType, 'intro' | 'warning'>;
	openErrorDrawer: () => void;
	openTransferFirstWarningModal: () => void;
}) => {
	const [privateKey, setPrivateKey] = useAtom(privateKeyHexAtom);
	const [contentToRender, setContentToRender] = useState<ExportAndDeleteAccountPageContentType>(initContent);

	// In React strict dev mode, useEffects are called twice, but we don't want two modals
	// so we need this ugly hack
	const shouldOpenWarningModal = useRef(true);

	usePauseCredentialsWebSocketsListener();

	useEffect(() => {
		if (shouldOpenWarningModal.current && initContent === 'warning') {
			openTransferFirstWarningModal();
			shouldOpenWarningModal.current = false;
		}

		if (initContent === 'intro') {
			setContentToRender('intro');
		}
	}, []);

	const handleClickExportAndClose = () => setContentToRender('confirmation');

	const loader = useAppLoader();
	const logout = useLogout();
	const { deleteAccount } = useDeleteAccount();

	const closeAccount = async () => {
		setPrivateKey('0x' + (await exportWallet()).privateKeyHexString);

		await deleteAccount();
		await logout(true);
	};

	const handleConfirm = async () => {
		try {
			await loader(closeAccount());
		} catch (error) {
			Logger.error('useExportAndCloseAccountPage failed in handleConfirm', { error });
			openErrorDrawer();

			return;
		}
		setContentToRender('completion');
	};
	const handleDismissWarning = () => setContentToRender('intro');

	return {
		contentToRender,
		handleClickExportAndClose,
		handleConfirm,
		handleDismissWarning,
		privateKey
	};
};
